import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageBanner from "@/components/base/PageBanner";
import JsonLd from "@/components/base/JsonLd";
import { mangaSeries, type MangaSeries } from "@/mocks/manga";

type FilterKey = "all" | "today" | "latest" | "backfill" | "hiatus";

const filters: { key: FilterKey; label: string; icon: string }[] = [
  { key: "all", label: "全部动态", icon: "ri-apps-2-line" },
  { key: "today", label: "今天更新", icon: "ri-flashlight-line" },
  { key: "latest", label: "最新话", icon: "ri-book-open-line" },
  { key: "backfill", label: "补更", icon: "ri-refresh-line" },
  { key: "hiatus", label: "断更", icon: "ri-pause-circle-line" },
];

function filterManga(list: MangaSeries[], key: FilterKey) {
  switch (key) {
    case "today":
      return list.filter((m) => m.isToday || m.isNew);
    case "latest":
      return list.filter((m) => m.isToday || m.isNew || m.isBackfill);
    case "backfill":
      return list.filter((m) => m.isBackfill);
    case "hiatus":
      return list.filter((m) => m.isOnHiatus);
    default:
      return list;
  }
}

function badgeInfo(m: MangaSeries) {
  if (m.isOnHiatus) return { text: "断更", cls: "bg-background-300 text-foreground-700" };
  if (m.isBackfill) return { text: "补更", cls: "bg-accent-500 text-background-50" };
  if (m.isNew) return { text: "新更", cls: "bg-primary-500 text-background-50" };
  if (m.isToday) return { text: "今天更新", cls: "bg-primary-500 text-background-50" };
  return { text: "更新中", cls: "bg-secondary-500 text-background-50" };
}

export default function UpdatesPage() {
  const [active, setActive] = useState<FilterKey>("all");

  const list = useMemo(
    () =>
      filterManga(
        [...mangaSeries].sort((a, b) => {
          const da = new Date(a.updateDate).getTime();
          const db = new Date(b.updateDate).getTime();
          return db - da;
        }),
        active
      ),
    [active]
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://897kg.cc/" },
      { "@type": "ListItem", position: 2, name: "更新动态", item: "https://897kg.cc/updates" },
    ],
  };

  return (
    <>
      <JsonLd id="jsonld-breadcrumb-updates" data={breadcrumbSchema} />
      <PageBanner
        eyebrow="追更必备"
        title="更新动态"
        desc="实时掌握66漫画网最新话、今天更新、补更与断更等动态，一键查看进度，追更不再错过。"
        crumbs={[{ label: "首页", to: "/" }, { label: "更新动态" }]}
      />

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setActive(f.key)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === f.key
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              <i className={f.icon}></i>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共 <span className="font-bold text-primary-600">{list.length}</span> 条更新动态
          </p>
          <p className="hidden text-sm text-foreground-500 sm:block">按更新时间排序 · 最近更新在前</p>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {list.map((m) => {
            const badge = badgeInfo(m);
            return (
              <Link
                key={m.id}
                to={`/series/${m.id}`}
                className="group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row sm:items-center"
              >
                <img
                  src={m.cover}
                  alt={`${m.title} 更新动态`}
                  className="h-28 w-20 shrink-0 rounded-lg object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.cls}`}>
                      {badge.text}
                    </span>
                    <span className="text-xs text-foreground-500">{m.category}</span>
                    <span className="text-xs text-foreground-500">{m.status}</span>
                  </div>
                  <h3 className="mt-2 truncate text-base font-bold text-foreground-900 group-hover:text-primary-600">
                    {m.title}
                  </h3>
                  <p className="mt-1 truncate text-sm text-primary-600">{m.lastChapter}</p>
                  <p className="mt-1 truncate text-xs text-foreground-500">
                    更新于 {m.updateDate} · 读者 {m.readers} · 评分 {m.rating.toFixed(1)}
                  </p>
                </div>
                <div className="shrink-0">
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-200 px-4 py-2 text-sm text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                    查看详情
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}